"use client";

import { faTriangleExclamation } from "@fortawesome/pro-solid-svg-icons";
import { Icon } from "@/components/ui/Icon";
import { useReportBoundaryError } from "@/lib/observability/report-client";

/*
 * Last-resort boundary: catches errors thrown by the locale layout itself. It
 * replaces the root layout, so it must render its own <html>/<body> and use
 * inline styles (globals.css may not have loaded).
 *
 * ⚠️ **`<html lang="ar" dir="rtl">` was hardcoded, so an English visitor got an
 * Arabic RTL document** (TRY-367). The copy here has always been deliberately
 * bilingual — `حدث خطأ غير متوقع` above "Something went wrong", and a button
 * reading "إعادة المحاولة · Try again" — so the document attributes claimed one
 * language for a page that is two.
 *
 * It is `dir="auto"` with **no `lang`** now, for the reason the fix cannot be
 * anything cleverer: this component replaces the root layout and receives no
 * params, so there is no locale to read. `auto` lets the browser resolve each run
 * of text by its own first strong character, which is right for both halves;
 * asserting `lang` would be picking one of the two.
 *
 * ⚠️ Nothing here reads Base UI direction — the transitive graph is four modules
 * with zero `useDirection` consumers — so this is NOT the TRY-136 provider gap and
 * a `DirectionProvider` must not be added to "match" it.
 */
export default function GlobalError({
	error,
	reset,
}: {
	error: Error & { digest?: string };
	reset: () => void;
}) {
	// TRY-418: the digest below is already on the customer's screen; this is what
	// puts a matching record on the server. Never throws — see the hook's header.
	//
	// ⚠️ **No `explained` flag here, unlike the root and panel boundaries, and
	// that is deliberate.** This boundary replaces the whole document, so it only
	// catches what the ROOT LAYOUT threw. A stale tab's refused server action is
	// raised by the router and caught by the nearest boundary below this one, so
	// an `UnrecognizedActionError` cannot arrive here; wiring the flag in would
	// read as coverage for a case that cannot reach it. Everything that does
	// reach this is a real failure with nothing else rendering, so it all gets
	// reported.
	useReportBoundaryError("global", error);

	return (
		<html dir="auto">
			<body
				style={{
					margin: 0,
					minHeight: "100vh",
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					fontFamily: "system-ui, sans-serif",
					background: "#0b0e14",
					color: "#e6e9f0",
					textAlign: "center",
				}}
			>
				<div style={{ maxWidth: 420, padding: 24 }}>
					<div style={{ fontSize: 40 }} aria-hidden>
						<Icon icon={faTriangleExclamation} size={14} />
					</div>
					<h1 style={{ fontSize: 20, margin: "12px 0 4px" }}>حدث خطأ غير متوقع</h1>
					<p style={{ fontSize: 14, opacity: 0.7, margin: "0 0 16px" }}>
						Something went wrong. Please try again.
					</p>
					{error.digest && (
						<p style={{ fontSize: 11, opacity: 0.5, fontFamily: "monospace" }}>
							ref: {error.digest}
						</p>
					)}
					<button
						onClick={() => reset()}
						style={{
							padding: "8px 20px",
							borderRadius: 8,
							border: "1px solid #2a3040",
							background: "#1a1f2b",
							color: "inherit",
							cursor: "pointer",
							fontSize: 14,
						}}
					>
						إعادة المحاولة · Try again
					</button>
				</div>
			</body>
		</html>
	);
}
